/**
 * TabPublicacion — Fase 4: Edición/Publicación.
 * 
 * - Gestor de copys con asistencia de Gemini
 * - Control de estado de publicación (Borrador, Aprobado, Programado, Publicado)
 * - Creación, edición y eliminación manual de copys
 * - Guardado directo de copys generados por IA
 * - Botón "Cerrar Mes"
 */

import { useEffect, useState } from 'react';
import {
  Send,
  Sparkles,
  FileCheck,
  Clock,
  CheckCircle2,
  Plus,
  Loader2,
  AlertTriangle,
  Archive,
  Copy,
  Check,
  Edit2,
  Trash2,
  X,
  Calendar,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import type { ContentCopy, ContentCalendarItem } from '@/types';

interface Props {
  clientId: string;
  clientName: string;
  businessType: string;
  period: string;
}

const PUB_STATUS_CONFIG: Record<
  string,
  { label: string; icon: React.ReactNode; variant: 'secondary' | 'warning' | 'info' | 'success' }
> = {
  draft: { label: 'Borrador', icon: <FileCheck size={14} />, variant: 'secondary' },
  approved: { label: 'Aprobado', icon: <CheckCircle2 size={14} />, variant: 'info' },
  scheduled: { label: 'Programado', icon: <Clock size={14} />, variant: 'warning' },
  published: { label: 'Publicado', icon: <Send size={14} />, variant: 'success' },
};

export function TabPublicacion({ clientId, clientName, businessType, period }: Props) {
  const [copies, setCopies] = useState<ContentCopy[]>([]);
  const [calendarItems, setCalendarItems] = useState<ContentCalendarItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copyTopic, setCopyTopic] = useState('');
  const [copyPlatform, setCopyPlatform] = useState('instagram');
  const [generatedCopy, setGeneratedCopy] = useState<string | null>(null);
  const [copiedAiText, setCopiedAiText] = useState(false);
  const [savingAiCopy, setSavingAiCopy] = useState(false);
  const [closingMonth, setClosingMonth] = useState(false);

  // Modal Crear / Editar Copy
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [editingCopy, setEditingCopy] = useState<ContentCopy | null>(null);
  const [copyForm, setCopyForm] = useState({
    platform: 'instagram',
    copy_text: '',
    content_calendar_id: '',
    publication_status: 'draft' as ContentCopy['publication_status'],
  });
  const [submittingCopy, setSubmittingCopy] = useState(false);

  const loadData = async () => {
    try {
      const [copiesRes, calRes] = await Promise.all([
        api.getContentCopies(clientId, period),
        api.getContentCalendar(clientId, period),
      ]);
      setCopies(copiesRes.copies as ContentCopy[]);
      setCalendarItems(calRes.calendar as ContentCalendarItem[]);
    } catch (err) {
      console.error('Error cargando copys y calendario:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadData();
  }, [clientId, period]);

  const handleGenerateCopy = async () => {
    if (!copyTopic) return;
    setGenerating(true);
    try {
      const res = await api.generateCopy({
        clientName,
        businessType,
        platform: copyPlatform,
        contentType: 'post',
        topic: copyTopic,
      });
      setGeneratedCopy(res.copy);
      setCopiedAiText(false);
    } catch (error) {
      console.error('Error generando copy:', error);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedAiText(true);
    setTimeout(() => setCopiedAiText(false), 2000);
  };

  const handleSaveAiCopy = async () => {
    if (!generatedCopy) return;
    setSavingAiCopy(true);
    try {
      await api.createContentCopy({
        client_id: clientId,
        period,
        platform: copyPlatform,
        copy_text: generatedCopy,
        ai_generated: true,
        publication_status: 'draft',
      });
      await loadData();
      alert('✅ Copy guardado en la lista del período');
    } catch (err) {
      console.error('Error guardando copy de IA:', err);
      alert('Error al guardar el copy');
    } finally {
      setSavingAiCopy(false);
    }
  };

  const openNewCopyModal = () => {
    setEditingCopy(null);
    setCopyForm({
      platform: copyPlatform || 'instagram',
      copy_text: '',
      content_calendar_id: '',
      publication_status: 'draft',
    });
    setCopyModalOpen(true);
  };

  const openEditCopyModal = (copy: ContentCopy) => {
    setEditingCopy(copy);
    setCopyForm({
      platform: copy.platform || 'instagram',
      copy_text: copy.copy_text || '',
      content_calendar_id: copy.content_calendar_id || '',
      publication_status: copy.publication_status || 'draft',
    });
    setCopyModalOpen(true);
  };

  const handleSaveCopy = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!copyForm.copy_text.trim()) return;

    setSubmittingCopy(true);
    try {
      if (editingCopy) {
        await api.updateContentCopy(editingCopy.id, {
          copy_text: copyForm.copy_text.trim(),
          publication_status: copyForm.publication_status,
          platform: copyForm.platform,
        });
      } else {
        await api.createContentCopy({
          client_id: clientId,
          period,
          platform: copyForm.platform,
          copy_text: copyForm.copy_text.trim(),
          content_calendar_id: copyForm.content_calendar_id || null,
          publication_status: copyForm.publication_status,
          ai_generated: false,
        });
      }
      setCopyModalOpen(false);
      await loadData();
    } catch (err) {
      console.error('Error guardando copy:', err);
      alert('Error guardando el copy');
    } finally {
      setSubmittingCopy(false);
    }
  };

  const handleDeleteCopy = async (id: string) => {
    if (!confirm('¿Eliminar este copy?')) return;
    try {
      await api.deleteContentCopy(id);
      setCopies((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error('Error eliminando copy:', err);
    }
  };

  const handleStatusChange = async (copy: ContentCopy, newStatus: ContentCopy['publication_status']) => {
    try {
      await api.updateContentCopy(copy.id, {
        copy_text: copy.copy_text,
        publication_status: newStatus,
        published_at: newStatus === 'published' ? new Date().toISOString() : null,
      });
      setCopies((prev) =>
        prev.map((c) => (c.id === copy.id ? { ...c, publication_status: newStatus } : c))
      );
    } catch (err) {
      console.error('Error actualizando estado del copy:', err);
    }
  };

  const handleCloseMonth = async () => {
    if (!confirm(`¿Cerrar el mes ${period} para ${clientName}? Esta acción no se puede deshacer.`)) {
      return;
    }

    setClosingMonth(true);
    try {
      const res = await api.closeMonth(clientId, period);
      alert(`✅ ${res.message}\n\n${res.summary}`);
    } catch (error) {
      console.error('Error cerrando mes:', error);
      alert('❌ Error al cerrar el mes');
    } finally {
      setClosingMonth(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ─── Generador de Copys con IA ─── */}
      <Card className="border-primary/20 shadow-xs">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles size={16} className="text-primary" />
            Generar Copy con IA
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="text"
              placeholder="Tema del copy (ej: Promoción Black Friday, Tips de belleza)"
              value={copyTopic}
              onChange={(e) => setCopyTopic(e.target.value)}
              className="md:col-span-2 rounded-xl border border-slate-200 bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            />
            <select
              value={copyPlatform}
              onChange={(e) => setCopyPlatform(e.target.value)}
              className="rounded-xl border border-slate-200 bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            >
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="tiktok">TikTok</option>
              <option value="linkedin">LinkedIn</option>
            </select>
          </div>
          <Button
            onClick={handleGenerateCopy}
            disabled={generating || !copyTopic}
            className="gap-2 shadow-xs"
          >
            {generating ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Sparkles size={16} />
            )}
            {generating ? 'Generando copys con IA...' : 'Generar Opciones'}
          </Button>

          {generatedCopy && (
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/70 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <Sparkles size={13} className="text-primary" />
                  Propuesta generada por Gemini
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleCopyClipboard(generatedCopy)}
                    className="h-7 text-xs gap-1.5"
                  >
                    {copiedAiText ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                    {copiedAiText ? '¡Copiado!' : 'Copiar texto'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveAiCopy}
                    disabled={savingAiCopy}
                    className="h-7 text-xs gap-1.5 bg-primary text-white"
                  >
                    {savingAiCopy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                    Guardar como Copy
                  </Button>
                </div>
              </div>
              <pre className="whitespace-pre-wrap text-sm font-sans text-slate-800 bg-white p-3.5 rounded-lg border border-slate-200 leading-relaxed">
                {generatedCopy}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Lista de Copys ─── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>Copys del Período ({copies.length})</span>
            <Button size="sm" onClick={openNewCopyModal} className="gap-1.5 shadow-xs text-xs">
              <Plus size={14} />
              Nuevo Copy
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {copies.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-3 text-slate-400">
                <Send size={20} />
              </div>
              <p className="text-sm font-medium text-slate-700">No hay copys registrados para este período</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                Genera copys con inteligencia artificial arriba o redacta uno manualmente.
              </p>
              <Button size="sm" variant="outline" onClick={openNewCopyModal} className="mt-4 gap-1 text-xs">
                <Plus size={12} /> Redactar primer copy
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {copies.map((copy) => {
                const statusConfig = PUB_STATUS_CONFIG[copy.publication_status] || PUB_STATUS_CONFIG.draft;
                return (
                  <div
                    key={copy.id}
                    className="rounded-xl border border-slate-200 p-4 hover:shadow-xs transition-all bg-white"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2.5">
                          <Badge variant="outline" className="capitalize text-xs font-semibold">
                            {copy.platform}
                          </Badge>
                          <Badge variant={statusConfig.variant} className="gap-1 text-xs">
                            {statusConfig.icon}
                            {statusConfig.label}
                          </Badge>
                          {copy.ai_generated ? (
                            <Badge variant="secondary" className="gap-1 text-[11px]">
                              <Sparkles size={10} className="text-primary" />
                              IA
                            </Badge>
                          ) : null}

                          {/* Selector rápido de estado */}
                          <select
                            value={copy.publication_status}
                            onChange={(e) =>
                              handleStatusChange(copy, e.target.value as ContentCopy['publication_status'])
                            }
                            className="text-[11px] font-medium bg-slate-50 border border-slate-200 rounded-md px-2 py-0.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-primary"
                          >
                            <option value="draft">Borrador</option>
                            <option value="approved">Aprobado</option>
                            <option value="scheduled">Programado</option>
                            <option value="published">Publicado ✓</option>
                          </select>
                        </div>

                        <p className="text-sm whitespace-pre-line text-slate-800 leading-relaxed">
                          {copy.copy_text}
                        </p>

                        {copy.calendar_title && (
                          <p className="text-xs text-primary font-medium mt-2 flex items-center gap-1.5">
                            <Calendar size={12} />
                            Vinculado a: <strong>{copy.calendar_title}</strong>
                          </p>
                        )}
                      </div>

                      {/* Botones de Acción */}
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleCopyClipboard(copy.copy_text)}
                          className="h-8 w-8 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                          title="Copiar texto"
                        >
                          <Copy size={13} />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEditCopyModal(copy)}
                          className="h-8 w-8 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                          title="Editar copy"
                        >
                          <Edit2 size={13} />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDeleteCopy(copy.id)}
                          className="h-8 w-8 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                          title="Eliminar copy"
                        >
                          <Trash2 size={13} />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Acción: Cerrar Mes ─── */}
      <Card className="border-destructive/30 bg-destructive/5">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2 text-destructive">
            <Archive size={16} />
            Cerrar Mes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Al cerrar el mes se creará la estructura de carpetas del nuevo período en Google Drive
            y se generará un resumen del mes actual.
          </p>
          <div className="flex items-center gap-3">
            <Button
              variant="destructive"
              onClick={handleCloseMonth}
              disabled={closingMonth}
              className="gap-2"
            >
              {closingMonth ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <AlertTriangle size={16} />
              )}
              Cerrar Mes {period}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ─── Modal Crear / Editar Copy ─── */}
      {copyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4 animate-in fade-in">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900 text-sm">
                {editingCopy ? 'Editar Copy' : 'Nuevo Copy'}
              </h3>
              <button
                type="button"
                onClick={() => setCopyModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSaveCopy} className="p-5 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">
                    Plataforma
                  </label>
                  <select
                    value={copyForm.platform}
                    onChange={(e) => setCopyForm({ ...copyForm, platform: e.target.value })}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
                  >
                    <option value="instagram">Instagram</option>
                    <option value="facebook">Facebook</option>
                    <option value="tiktok">TikTok</option>
                    <option value="linkedin">LinkedIn</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">
                    Estado de Publicación
                  </label>
                  <select
                    value={copyForm.publication_status}
                    onChange={(e) =>
                      setCopyForm({
                        ...copyForm,
                        publication_status: e.target.value as ContentCopy['publication_status'],
                      })
                    }
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
                  >
                    <option value="draft">Borrador</option>
                    <option value="approved">Aprobado</option>
                    <option value="scheduled">Programado</option>
                    <option value="published">Publicado</option>
                  </select>
                </div>
              </div>

              {calendarItems.length > 0 && !editingCopy && (
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">
                    Asociar a Publicación del Calendario (Opcional)
                  </label>
                  <select
                    value={copyForm.content_calendar_id}
                    onChange={(e) => setCopyForm({ ...copyForm, content_calendar_id: e.target.value })}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
                  >
                    <option value="">Sin asociar (Copy general)</option>
                    {calendarItems.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.scheduled_date} — {item.content_type?.toUpperCase()}: {item.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Texto del Copy <span className="text-rose-500">*</span>
                </label>
                <textarea
                  required
                  rows={6}
                  placeholder="Escribe el texto de la publicación, hashtags y llamado a la acción..."
                  value={copyForm.copy_text}
                  onChange={(e) => setCopyForm({ ...copyForm, copy_text: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCopyModalOpen(false)}
                  className="text-xs"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={submittingCopy}
                  className="text-xs bg-primary hover:bg-primary/90 text-white"
                >
                  {submittingCopy ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : editingCopy ? (
                    'Guardar Cambios'
                  ) : (
                    'Crear Copy'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
